import React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router"

import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useNotification } from "@/contexts/notification-context"
import type { Organization } from "@/models/organization"
import type { EditCredentialSchema } from "@/modules/admin/credentials"
import {
  CredentialIdentityReadonlyFields,
  CredentialNotesField,
  CredentialSecretFields,
  CredentialWebsiteField,
  editCredentialSchema,
  getCredentialSubmitDetail,
  getCredentialSubmitErrorKey,
  useConfirmDeleteCredential,
} from "@/modules/admin/credentials"
import { adminEntityFieldsMaxWidthSx } from "@/modules/admin/utils/admin-entity-fields-max-width-sx"
import { Form } from "@/nzds/forms/form/form"
import { ResetButton } from "@/nzds/forms/reset-button/reset-button"
import { SubmitButton } from "@/nzds/forms/submit-button/submit-button"
import { useForm } from "@/nzds/forms/use-form/use-form"
import type { CredentialMeta } from "@/services/credential"
import { useUpdateCredential } from "@/services/credential"
import { useUrl } from "@/utils/url"

import { Link as MuiLink } from "@mui/material"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Collapse from "@mui/material/Collapse"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

interface AdminCredentialEditFormProps {
  credential: CredentialMeta
  organizationId: Organization["id"]
}

export const AdminCredentialEditForm: React.FC<
  AdminCredentialEditFormProps
> = ({ credential, organizationId }) => {
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { setNotification } = useNotification()
  const { adminCredentialPath, adminCredentialsPath } = useUrl()
  const [submitErrorMessage, setSubmitErrorMessage] = React.useState<
    string | null
  >(null)

  const form = useForm({
    defaultValues: {
      notes: credential.notes ?? "",
      password: "",
      username: credential.username,
      website: credential.website ?? "",
    },
    // Keep Save eligibility current while the password field has focus.
    mode: "onTouched",
    schema: editCredentialSchema,
  })

  const { updateCredentialIsPending, updateCredentialMutateAsync } =
    useUpdateCredential()

  const { confirmAndDeleteCredential, deleteCredentialIsPending } =
    useConfirmDeleteCredential({
      organizationId,
      onSuccess: () => {
        void navigate(adminCredentialsPath, { replace: true })
      },
    })

  const navigateToShow = () => {
    void navigate(adminCredentialPath(credential.id), { replace: true })
  }

  const handleSubmit: SubmitHandler<EditCredentialSchema> = async (values) => {
    setSubmitErrorMessage(null)

    try {
      await updateCredentialMutateAsync({
        credentialId: credential.id,
        organizationId,
        payload: {
          notes: values.notes,
          password: values.password,
          providerName: credential.providerName,
          username: values.username,
          website: values.website,
        },
      })
      navigateToShow()
    } catch (error: unknown) {
      const errorKey = getCredentialSubmitErrorKey(error, "update")
      setSubmitErrorMessage(getCredentialSubmitDetail(error) ?? t(errorKey))

      if (errorKey === "admin.credentialsUi.updateError") {
        setNotification({
          message: t(errorKey),
          type: "error",
        })
      }
    }
  }

  const handleCancel = () => {
    if (!updateCredentialIsPending && !deleteCredentialIsPending) {
      setSubmitErrorMessage(null)
      navigateToShow()
    }
  }

  const handleDeleteClick = () => {
    confirmAndDeleteCredential({
      id: credential.id,
      providerName: credential.providerName,
    }).catch(() => undefined)
  }

  const areActionsDisabled =
    updateCredentialIsPending || deleteCredentialIsPending

  return (
    <Form<EditCredentialSchema>
      aria-label={t("admin.credentialsUi.editDialog.title")}
      form={form}
      onSubmit={handleSubmit}
    >
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <>
              <MuiLink
                component={Link}
                to={adminCredentialsPath}
                underline="hover"
              >
                {t("admin.credentials")}
              </MuiLink>
              <MuiLink
                component={Link}
                to={adminCredentialPath(credential.id)}
                underline="hover"
              >
                {t("admin.credentialDetail.viewCredential")}
              </MuiLink>
            </>
          }
          heading={t("admin.credentialsUi.editDialog.title")}
          primaryActions={
            <>
              <ResetButton
                disabled={areActionsDisabled}
                onClick={handleCancel}
                variant="outlined"
              >
                {t("button.cancel")}
              </ResetButton>
              <SubmitButton
                disabled={areActionsDisabled}
                loading={updateCredentialIsPending}
              >
                {t("button.save")}
              </SubmitButton>
            </>
          }
          secondaryActions={
            <Button
              color="error"
              disabled={areActionsDisabled}
              onClick={handleDeleteClick}
            >
              {t("button.delete")}
            </Button>
          }
        />
        <PageCard>
          <Stack sx={{ gap: 2, pb: 1 }}>
            <Collapse in={Boolean(submitErrorMessage)} unmountOnExit>
              <Alert severity="error">{submitErrorMessage}</Alert>
            </Collapse>
            <Typography variant="body2">
              {t("admin.credentialsUi.purposeDescription")}
            </Typography>
            <Stack sx={{ ...adminEntityFieldsMaxWidthSx, gap: 2 }}>
              <CredentialIdentityReadonlyFields
                providerName={credential.providerName}
                resources={credential.resources}
              />
              <CredentialWebsiteField />
              <CredentialSecretFields />
              <CredentialNotesField />
            </Stack>
          </Stack>
        </PageCard>
      </Stack>
    </Form>
  )
}
